import React, { useEffect, useRef } from "react";
import {
  FaShieldAlt,
  FaGavel,
  FaCheckCircle,
  FaUserShield,
  FaMobileAlt,
  FaCoins,
  FaHandshake,
  FaFileContract,
  FaInfoCircle,
  FaEnvelope,
  FaPhone,
  FaClock,
  FaGlobe,
  FaHeart,
  FaUsers,
  FaUserMd,
} from "react-icons/fa";
import Navbar from "../../components/Navbar/Navbar";
import Footer from "../../components/Footer/Footer";
import ScrollToTop from "../../components/ScrollToTop/ScrollToTop";
import { ROUTES } from "../../constants/routes";
import styles from "./Terms.module.css";

const TermsPage: React.FC = () => {
  const observerRef = useRef<IntersectionObserver | null>(null);

  useEffect(() => {
    const observerOptions = {
      threshold: 0.2,
      rootMargin: "0px 0px -20px 0px",
    };

    observerRef.current = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting) {
          entry.target.classList.add("revealed");
        }
      });
    }, observerOptions);

    const elementsToObserve = document.querySelectorAll(
      ".scroll-reveal, .scroll-reveal-left, .scroll-reveal-right, .scroll-reveal-scale"
    );

    elementsToObserve.forEach((el) => {
      if (observerRef.current) {
        observerRef.current.observe(el);
      }
    });

    return () => {
      if (observerRef.current) {
        observerRef.current.disconnect();
      }
    };
  }, []);

  // Reduced content to match Privacy Policy performance
  const termsData = [
    {
      id: "acceptance",
      icon: <FaFileContract />,
      title: "Acceptance of Terms",
      content: [
        "By downloading, accessing, or using the LicoMeds mobile application, you agree to be bound by these Terms and Conditions.",
        "If you do not agree to these terms, please do not use our services.",
        "These terms apply to all users including patients, healthcare providers, laboratories, and pharmacies.",
      ],
    },
    {
      id: "platform",
      icon: <FaMobileAlt />,
      title: "Platform Description",
      content: [
        "LicoMeds is a mobile healthcare platform that connects patients with licensed healthcare professionals, laboratories, and pharmacies.",
        "All healthcare services are provided through our mobile application only.",
        "The website serves informational purposes and donation processing only.",
      ],
    },
    {
      id: "cashback",
      icon: <FaCoins />,
      title: "Fee Cashback System",
      content: [
        "Patients earn up to 50% cashback from transaction fees paid to healthcare providers on the platform.",
        "Cashback is calculated from platform transaction fees, not from the total service cost paid to healthcare providers.",
        "Fee cashback distribution: 50% to patient, 20% to referrer (if applicable), 30% to LicoMeds platform.",
        "Cashback earned from transaction fees can be used for payments, donations, or withdrawals subject to minimum thresholds.",
      ],
    },
    {
      id: "users",
      icon: <FaUsers />,
      title: "User Responsibilities",
      content: [
        "Patients must provide accurate medical information and follow prescribed treatments.",
        "Healthcare providers must maintain valid licenses and provide quality care.",
        "All users must respect platform guidelines and treat others with courtesy.",
        "Fraudulent activities or misuse of the platform will result in account termination.",
      ],
    },
    {
      id: "healthcare",
      icon: <FaUserMd />,
      title: "Healthcare Services",
      content: [
        "Healthcare providers maintain full control over consultation sessions and can suspend or end sessions as needed.",
        "Doctors can refer patients to specialists or laboratories through the platform.",
        "Prescriptions issued through the platform are legally valid and accepted by network pharmacies.",
        "Emergency medical situations should be directed to local emergency services immediately.",
      ],
    },
    {
      id: "payments",
      icon: <FaHandshake />,
      title: "Payments and Fees",
      content: [
        "All healthcare providers agree to transparent fee structures before joining the platform.",
        "Healthcare providers receive payment for their services separately from platform transaction fees.",
        "Platform transaction fees are used to fund the fee cashback system and platform operations.",
        "All fees and charges are clearly disclosed before transactions.",
      ],
    },
    {
      id: "privacy",
      icon: <FaUserShield />,
      title: "Privacy and Data Protection",
      content: [
        "Medical records and personal information are encrypted and securely stored.",
        "Health information is only shared with authorized healthcare providers during consultations.",
        "Users have complete control over their data and can request deletion at any time.",
        "Personal information is never sold or shared with third parties without explicit consent.",
      ],
    },
    {
      id: "community",
      icon: <FaHeart />,
      title: "Community Support",
      content: [
        "Users can donate their fee cashback to help other patients with medical expenses.",
        "Donations through the website are processed via secure fintech partners.",
        "Community aid requests must be made through the mobile application.",
        "LicoMeds reserves the right to verify and approve aid requests to prevent fraud.",
      ],
    },
  ];

  return (
    <>
      <Navbar />
      <ScrollToTop />

      <div className={styles.termsPage}>
        {/* Hero Section */}
        <section className={styles.hero}>
          <div className={styles.container}>
            <div className={styles.heroContent}>
              <div className={styles.heroBadge}>
                <FaGavel className={styles.badgeIcon} />
                Terms & Conditions
              </div>

              <h1 className={styles.heroTitle}>
                Terms and
                <span className={styles.heroAccent}> Conditions</span>
              </h1>

              <p className={styles.heroSubtitle}>
                Please read these terms carefully before using the{" "}
                {ROUTES.COMPANY.NAME} platform. These terms govern your use of
                our mobile healthcare services, fee cashback system, and
                community support features.
              </p>

              <div className={styles.heroInfo}>
                <div className={styles.infoItem}>
                  <FaClock className={styles.infoIcon} />
                  <div>
                    <span className={styles.infoLabel}>Last Updated</span>
                    <span className={styles.infoValue}>January 2025</span>
                  </div>
                </div>
                <div className={styles.infoItem}>
                  <FaGlobe className={styles.infoIcon} />
                  <div>
                    <span className={styles.infoLabel}>Applicable Region</span>
                    <span className={styles.infoValue}>Nigeria & Africa</span>
                  </div>
                </div>
              </div>
            </div>

            <div className={styles.heroVisual}>
              <div
                className={`${styles.heroImageContainer} ${styles.floatGentle}`}
              >
                <div className={`${styles.heroIcon} ${styles.heartbeatFlow}`}>
                  <FaShieldAlt />
                </div>
                <div className={styles.heroRipples}>
                  <div className={styles.ripple}></div>
                  <div className={styles.ripple}></div>
                  <div className={styles.ripple}></div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Quick Navigation */}
        <section className={styles.quickNav}>
          <div className={styles.container}>
            <h3 className={styles.quickNavTitle}>Quick Navigation</h3>
            <div className={styles.quickNavGrid}>
              {termsData.map((section) => (
                <a
                  key={section.id}
                  href={`#${section.id}`}
                  className={styles.quickNavItem}
                >
                  {section.icon}
                  <span>{section.title}</span>
                </a>
              ))}
            </div>
          </div>
        </section>

        {/* Terms Content */}
        <section className={styles.termsContent}>
          <div className={styles.container}>
            {termsData.map((section, index) => (
              <div
                key={section.id}
                id={section.id}
                className={`${styles.termsSection} scroll-reveal`}
              >
                <div className={styles.sectionHeader}>
                  <div className={styles.sectionIcon}>{section.icon}</div>
                  <h2 className={styles.sectionTitle}>
                    {index + 1}. {section.title}
                  </h2>
                </div>
                <div className={styles.sectionContent}>
                  {section.content.map((paragraph, idx) => (
                    <p key={idx} className={styles.paragraph}>
                      <FaCheckCircle className={styles.bulletIcon} />
                      {paragraph}
                    </p>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </section>

        {/* Contact Section */}
        <section className={styles.contactSection}>
          <div className={styles.container}>
            <div className={`${styles.contactContent} scroll-reveal`}>
              <div className={styles.contactIcon}>
                <FaInfoCircle />
              </div>
              <h2 className={styles.contactTitle}>
                Questions About These Terms?
              </h2>
              <p className={styles.contactSubtitle}>
                If you have any questions about these Terms and Conditions,
                please contact our support team. We're here to help clarify any
                aspects of our service agreement.
              </p>
              <div className={styles.contactActions}>
                <a href={ROUTES.EXTERNAL.EMAIL} className={styles.contactBtn}>
                  <FaEnvelope />
                  <span>Email Support</span>
                </a>
                <a
                  href={ROUTES.EXTERNAL.EMERGENCY_HOTLINE}
                  className={styles.contactBtn}
                >
                  <FaPhone />
                  <span>{ROUTES.EXTERNAL.EMERGENCY_DISPLAY}</span>
                </a>
              </div>
              <div className={styles.contactNote}>
                <FaCheckCircle />
                <small>
                  Available 24/7 • Response within 24 hours • Nigeria-based
                  support
                </small>
              </div>
            </div>
          </div>
        </section>
      </div>

      <Footer />
    </>
  );
};

export default TermsPage;
